import React, { useState } from "react";
import api, { formatApiError, uploadFileToStorage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, Paperclip, Music, Layers, Check } from "lucide-react";
import { toast } from "sonner";

const SLOTS = [
  { kind: "wav", label: "WAV master", icon: Music, accept: ".wav,audio/wav,audio/x-wav", has: (t) => t.wav_available },
  { kind: "stems", label: "Stems (ZIP)", icon: Layers, accept: ".zip,application/zip,.rar,.7z", has: (t) => t.stems_available },
];

function AttachSlot({ track, slot, onDone, onClose }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const Icon = slot.icon;

  const upload = async () => {
    if (!file) return;
    if (slot.kind === "wav" && !/\.wav$/i.test(file.name)) { toast.error("Please choose a .wav file"); return; }
    setBusy(true); setPct(0);
    try {
      const ref = await uploadFileToStorage(file, slot.kind, setPct);
      const { data } = await api.post(`/admin/tracks/${track.id}/attach`, {
        kind: slot.kind, path: ref.path, filename: ref.filename || file.name, content_type: ref.content_type || file.type,
      });
      const msg = data.notified.length ? ` · emailed ${data.notified.join(", ")}` : "";
      toast.success(`${slot.label} attached to “${track.title}”${msg}`);
      if (data.notify_failed.length) toast.error(`Couldn't email ${data.notify_failed.join(", ")} — they can still download from My Purchases`);
      setFile(null); onClose(); onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-[#2a2a2a] p-3">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-medium flex items-center gap-2"><Icon className="w-4 h-4 text-[#e2ff31]" />{slot.label}</p>
        {slot.has(track)
          ? <span data-testid={`attach-${slot.kind}-status-${track.id}`} className="text-xs text-[#22c55e] flex items-center gap-1"><Check className="w-3 h-3" />Attached · upload to replace</span>
          : <span data-testid={`attach-${slot.kind}-status-${track.id}`} className="text-xs text-[#6b7280]">Not attached (optional)</span>}
      </div>
      <input data-testid={`attach-${slot.kind}-input-${track.id}`} type="file" accept={slot.accept} disabled={busy}
        onChange={(e) => setFile(e.target.files[0] || null)}
        className="block w-full text-sm text-[#9ca3af] file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border-0 file:bg-[#1e1e1e] file:text-[#e2ff31] file:font-semibold hover:file:bg-[#2a2a2a] cursor-pointer" />
      {busy && (
        <div className="mt-2 h-1.5 rounded-full bg-[#0a0a0a] overflow-hidden">
          <div data-testid={`attach-${slot.kind}-progress-${track.id}`} className="h-full bg-[#e2ff31] transition-[width] duration-200" style={{ width: `${pct}%` }} />
        </div>
      )}
      <Button data-testid={`attach-${slot.kind}-submit-${track.id}`} onClick={upload} disabled={!file || busy} size="sm"
        className="mt-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] font-semibold">
        {busy ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{pct}%</> : `Upload ${slot.label}`}
      </Button>
    </div>
  );
}

export const AttachFilesButton = ({ track, onChanged }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button data-testid={`attach-files-${track.id}`} onClick={() => setOpen(true)} title="Attach WAV / Stems"
        className="p-2 rounded-lg text-[#9ca3af] hover:text-[#e2ff31] hover:bg-[#1e1e1e] transition-colors">
        <Paperclip className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`attach-dialog-${track.id}`} className="bg-[#121212] border-[#2a2a2a] text-[#f3f4f6] max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Attach WAV / Stems</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-[#9ca3af]">
            {track.sold
              ? "This beat is sold — the buyer's My Purchases updates instantly and they get an email with fresh download links."
              : "Buyers get every file attached here with their exclusive purchase."}
          </DialogDescription>
          <div className="space-y-3">
            {SLOTS.map((s) => <AttachSlot key={s.kind} track={track} slot={s} onDone={onChanged} onClose={() => setOpen(false)} />)}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
